/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, ChangeEvent, useEffect, useContext, forwardRef } from 'react';
import { TextField, Grid, Alert, Typography, Box, Divider, FormControl, InputLabel, FormHelperText, InputBase } from '@mui/material';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { numberSchema, nameSchema, emailSchema } from '../../models/schemas';
import { E164Number } from 'libphonenumber-js';
import { ValidationError } from 'yup';
import axios from 'axios'
import { PrimaryButton } from '../Button';
import { SimpleOrder } from '../../models/api';
import { ProductContext } from '../../context/products-context';
import { API_BASE_URL } from '../../constants';
import logger from '../../utils/logger';

interface FormProps {
  getTotal: () => number,
  clearCart: () => void,
  setToast: React.Dispatch<React.SetStateAction<{ show: boolean; severity: 'success' | 'error'; message: string }>>,
  formId?: string,
  onSubmittingChange?: (submitting: boolean) => void,
  layout?: 'desktop' | 'mobile',
}

const PhoneInputField = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const { color, size, ...rest } = props;
  return (
    <InputBase
      {...rest}
      inputRef={ref}
      sx={{
        flex: 1,
        minWidth: 0,
        border: 'none',
        '& .MuiInputBase-input': { py: 1.5 },
      }}
    />
  );
});

const SendOrderForm = (props: FormProps) => {
  const { getTotal, clearCart, setToast, formId = 'send-order-form', onSubmittingChange, layout = 'desktop' } = props;
  const isMobileLayout = layout === 'mobile';
  
  const [phoneNumber, setPhoneNumber] = useState<E164Number | string>('');
  const [preferredName, setPreferredName] = useState<string | undefined>('');
  const [numberIsValid, setNumberIsValid] = useState(true);
  const [numberIsInvalid, setNumberIsInvalid] = useState(false);
  const [numberIsValiding, setNumberIsValiding] = useState(false);
  const [numberErrorMessage, setNumberErrorMessage] = useState('Enter a valid phone number');

  const [email, setEmail] = useState('');
  const [emailIsValid, setEmailIsValid] = useState(false);
  const [emailIsInvalid, setEmailIsInvalid] = useState(false);
  const [emailIsValiding, setEmailIsValiding] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('Email is required');

  const [nameIsValid, setNameIsValid] = useState(false)
  const [nameIsInvalid, setNameIsInvalid] = useState(false)
  const [nameIsValiding, setNameIsValiding] = useState(false)
  const [nameErrorMessage, setNameErrorMessage] = useState('Preferred Name is required')

  const [isSubmitting, setIsSubmitting] = useState(false);
  React.useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const api = axios.create({ baseURL: API_BASE_URL });

  const { cartItems } = useContext(ProductContext);
  useEffect(() => {
    if (!isSubmitting) return;
    if (!(nameIsValid && emailIsValid)) {
      setIsSubmitting(false);
      return;
    }
    const numberString = phoneNumber?.toString() || '';
    if (numberString && !numberIsValid) {
      setIsSubmitting(false);
      return;
    }
      const preferredNameString = (preferredName || '').trim();
      const productNames = Object.keys(cartItems);
      const cartProducts = productNames.filter(product => cartItems[product])
      const cart = cartProducts.map(product => ({
        productName: product,
        quantity: cartItems[product]
      }))
      const order: SimpleOrder = {
        preferredName: preferredNameString,
        email: email.trim(),
        cart,
        ...(numberString ? { phoneNumber: numberString } : {}),
      };

      const sendOrder = async (order: SimpleOrder) => {
        const cartSize = cart.length;
        const total = getTotal();
        logger.info('order.submit.start', { 
          cartSize, 
          total,
        });
        
        try {
          const response = await api.post(
            '/order',
            order
          );
      
          if (response.status === 200) {
            logger.info('order.submit.success', { 
              cartSize,
              total,
              orderId: response.data?.orderId 
            });
            clearCart();
            setToast({
              show: true,
              severity: 'success',
              message: 'Order sent. Check your email for a summary — we will contact you soon to finalise payment and delivery.',
            });
          } else {
            logger.warn('order.submit.unexpected_status', { status: response.status, cartSize, total });
            setIsSubmitting(false);
            setToast({ show: true, severity: 'error', message: 'Something went wrong. Please try again.' });
          }
        } catch (error: any) {
          logger.error('order.submit.failed', error, {
            cartSize,
            total,
            email: email.trim() ? `${email.trim().slice(0, 2)}***` : undefined,
            phoneNumber: numberString ? '***-***-' + numberString.slice(-4) : undefined,
          });
          setIsSubmitting(false);
          setToast({ show: true, severity: 'error', message: 'Failed to send order. Please check your connection and try again.' });
        } finally {
          setIsSubmitting(false);
        }
      };
      sendOrder(order);
  }, [
    nameIsValiding,
    emailIsValiding,
    numberIsValiding,
    isSubmitting,
    email,
  ])


  const handleEmailChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmailIsValiding(true);
    try {
      await emailSchema.validate(value);
      setEmailIsValid(true);
      setEmailIsInvalid(false);
    } catch (err) {
      setEmailIsValid(false);
      setEmailIsInvalid(true);
      if (err instanceof ValidationError) {
        setEmailErrorMessage(err.message);
      }
    }
    setEmailIsValiding(false);
    setEmail(value);
  };

  const handleNameChange = async (event: ChangeEvent<any>) => {
    const { value } = event.target;
    setNameIsValiding(true)
    try {
      await nameSchema.validate(value)
      setNameIsValid(true);
      setNameIsInvalid(false)
    } catch (error) {
      setNameIsValid(false);
      setNameIsInvalid(true);
      if (error instanceof ValidationError) {
        setNameErrorMessage(error.message);
      }
    }
    setNameIsValiding(false)
    setPreferredName(value);
  };
  
  const handleNumberChange = async (value: E164Number | undefined) => {
    setNumberIsValiding(true);
    if (!value) {
      setNumberIsValid(true);
      setNumberIsInvalid(false);
      setPhoneNumber('');
      setNumberIsValiding(false);
      return;
    }
    try {
      await numberSchema.validate(value);
      setNumberIsValid(true);
      setNumberIsInvalid(false);
    } catch (error) {
      setNumberIsValid(false);
      setNumberIsInvalid(true);
      if (error instanceof ValidationError) {
        setNumberErrorMessage(error.message);
      }
    }
    setNumberIsValiding(false);
    setPhoneNumber(value);
  };
  
  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    if (!nameIsValid) {
      setNameIsInvalid(true);
    }
    if (!emailIsValid) {
      setEmailIsInvalid(true);
    }
    const numberString = phoneNumber?.toString() || '';
    if (numberString && !numberIsValid) {
      setNumberIsInvalid(true);
    }
  };

  const formFields = (
    <>
      <Alert
        severity="info"
        sx={{
          mb: 1.5,
          py: 1,
          px: 1.5,
          backgroundColor: 'rgba(33, 150, 243, 0.12)',
          color: 'info.dark',
          '& .MuiAlert-icon': { color: 'info.main' },
          '& .MuiAlert-message': { fontSize: 14 },
        }}
      >
        We will email you a summary of this order. Order (including payment and delivery details) will be finalised on WhatsApp or email.
      </Alert>
      <Grid container spacing={2}>
        <Grid size={12}>
            <TextField
              label="Preferred Name"
              placeholder="Neo"
              value={preferredName}
              required
              onChange={handleNameChange}
              error={nameIsInvalid}
              helperText={nameIsInvalid ? nameErrorMessage : ''}
              fullWidth
              variant="outlined"
            />
        </Grid>
        <Grid size={12}>
          <TextField
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            required
            onChange={handleEmailChange}
            error={emailIsInvalid}
            helperText={emailIsInvalid ? emailErrorMessage : ''}
            fullWidth
            variant="outlined"
            autoComplete="email"
          />
        </Grid>
        <Grid size={12}>
          <FormControl fullWidth error={numberIsInvalid} variant="outlined">
            <InputLabel shrink htmlFor="whatsapp-phone">
              Whatsapp number
            </InputLabel>
            <Box
              id="whatsapp-phone"
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                border: '1px solid',
                borderColor: numberIsInvalid ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                overflow: 'hidden',
                mt: 1.5,
                minHeight: 56,
                '&:hover': {
                  borderColor: numberIsInvalid ? 'error.main' : 'rgba(0, 0, 0, 0.87)',
                },
                '&:focus-within': {
                  borderColor: numberIsInvalid ? 'error.main' : 'primary.main',
                  boxShadow: (theme) => `0 0 0 1px ${numberIsInvalid ? theme.palette.error.main : theme.palette.primary.main}`,
                },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', pl: 1.5 }}>
                <PhoneInput
                  placeholder="012 345 6789"
                  value={phoneNumber}
                  onChange={handleNumberChange}
                  defaultCountry="ZA"
                  inputComponent={PhoneInputField}
                />
              </Box>
            </Box>
            <FormHelperText error={numberIsInvalid}>
              {numberIsInvalid ? numberErrorMessage : ''}
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );

  if (isMobileLayout) {
    return (
      <form id={formId} onSubmit={(e) => { e.preventDefault(); handleSubmit(e as unknown as React.MouseEvent); }}>
        <Box sx={{ px: 2, pt: 2, pb: 'max(16px, env(safe-area-inset-bottom))', backgroundColor: 'background.paper' }}>
          {formFields}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Total
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              R{getTotal()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <PrimaryButton
              width="100%"
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Order'}
            </PrimaryButton>
          </Box>
        </Box>
      </form>
    );
  }

  return (
    <form id={formId} onSubmit={(e) => { e.preventDefault(); handleSubmit(e as unknown as React.MouseEvent); }}>
      <Box sx={{ p: 2 }}>
        {formFields}
        <Divider sx={{ opacity: 0.8, my: 2 }} />
        <Grid container spacing={2}>
          <Grid size={6}>
            <Typography variant="h5">Total:</Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="h5" align='right'>R{getTotal()}</Typography>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2 }}>
          <PrimaryButton
            width='100%'
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Order'}
          </PrimaryButton>
        </Box>
      </Box>
    </form>
  );
};

export default SendOrderForm;